import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  fetchIndividualSubmissionData,
  getReviewColumns,
  resubmitSubmissionData,
} from "../../../services/api";
import APP_CONSTANTS from "../../../utils/config";
import { useDispatch } from "react-redux";
import { updateReviewColumnData } from "../../../redux/slice/brainSlice";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ExcelJS from "exceljs";
import { brainStyles } from "../../../theme/style";
import { useTheme } from "@mui/styles";
import Fuse from "fuse.js";
import {
  getLocalStorageData,
  getParsedValue,
  isRestrictedKey,
  isTagKey,
  parseTagValue,
  setLocalStorageData,
  valueToString,
} from "../components/reviewColumnUtils";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import StatusChip from "../components/StatusChip";
import { DownloadIcon } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Close as CloseIcon,
  LockOutlined as LockOutlinedIcon,
  InboxOutlined as InboxOutlinedIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import ColumnVisibilityControl from "../components/ColumnVisibilityControl";
import TagInput from "../components/TagInput";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SKUDetailsDrawer from "../components/SKUDetailsDrawer";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error/Error";

function useTableColumns(columnData) {
  const [visibleColumns, setVisibleColumns] = useState(new Set([]));
  const [columnOrder, setColumnOrder] = useState([]);
  useEffect(() => {
    if (!columnData?.columns) return;
    const all_columns = columnData.columns;
    const all_column_list = all_columns.map((c) => c.key);
    const user_editable_columns = all_columns
      .filter((c) => c.user_edit_access === true)
      .map((c) => c.key);
    let visibleColumn_data = getLocalStorageData(APP_CONSTANTS.VISIBLE_COLUMN);
    let columnOrderData = getLocalStorageData(APP_CONSTANTS.COLUMN_ORDER);
    const isSame =
      all_column_list.length === columnOrderData.length &&
      all_column_list.every((item) => columnOrderData.includes(item));
    const isIncluded = user_editable_columns.every((item) =>
      visibleColumn_data.includes(item)
    );
    if (
      !visibleColumn_data?.length ||
      !columnOrderData?.length ||
      !isSame ||
      !isIncluded
    ) {
      const non_editable_columns = all_columns
        .filter((item) => item.is_editable_column === false)
        .map((item) => item.key);
      const user_non_editable_columns = all_columns
        .filter(
          (item) =>
            item.is_editable_column === true && item.user_edit_access === false
        )
        .map((item) => item.key);
      visibleColumn_data = [
        ...(columnData.key_reference_columns || []),
        ...user_editable_columns,
      ];
      columnOrderData = [
        ...non_editable_columns,
        ...user_editable_columns,
        ...user_non_editable_columns,
      ];
      setLocalStorageData(APP_CONSTANTS.VISIBLE_COLUMN, visibleColumn_data);
      setLocalStorageData(APP_CONSTANTS.COLUMN_ORDER, columnOrderData);
    }
    if (visibleColumns.size === 0 || columnOrder.length === 0) {
      setVisibleColumns(new Set(visibleColumn_data));
      setColumnOrder(columnOrderData);
    }
  }, [columnData, columnOrder.length, visibleColumns.size]);
  return { visibleColumns, setVisibleColumns, columnOrder, setColumnOrder };
}

function useBatchData(batch, setSelectedBatch) {
  const counts = useMemo(() => {
    const rows = batch?.data?.rows || [];
    return {
      all: rows.length,
      pending: rows.filter((s) => s.status === "pending").length,
      approved: rows.filter((s) => s.status === "approved").length,
      rejected: rows.filter((s) => s.status === "rejected").length,
    };
  }, [batch?.data?.rows]);
  const visibleRows = useMemo(() => {
    const rows = batch?.data?.rows || [];
    return {
      all: rows,
      pending: rows.filter((s) => s.status === "pending"),
      approved: rows.filter((s) => s.status === "approved"),
      rejected: rows.filter((s) => s.status === "rejected"),
    };
  }, [batch?.data?.rows]);
  const handleCellChange = useCallback(
    (row, colKey, nextValue) => {
      const predictedValue = row?.snapshot[colKey];
      setSelectedBatch((prevBatch) => {
        if (!prevBatch?.data?.rows) return prevBatch;
        return {
          ...prevBatch,
          data: {
            ...prevBatch.data,
            rows: prevBatch.data.rows.map((item) => {
              if (item.rowId !== row.rowId) return item;
              let newChanges = [...(item.changes || [])];
              let existingChangeIndex = newChanges.findIndex(
                (c) => c.column === colKey
              );
              let existingChange =
                existingChangeIndex >= 0
                  ? newChanges[existingChangeIndex]
                  : null;
              const safeStr = (v) =>
                v === null || v === undefined ? "" : String(v);
              if (!existingChange) {
                let cur_value = item?.snapshot[colKey];
                if (safeStr(nextValue) !== safeStr(cur_value)) {
                  newChanges.push({
                    column: colKey,
                    oldValue: cur_value,
                    editedValue: nextValue,
                    is_new_object: true,
                    predictedValue: predictedValue,
                  });
                }
              } else {
                if (existingChange.is_new_object) {
                  if (safeStr(nextValue) === safeStr(existingChange.oldValue)) {
                    newChanges.splice(existingChangeIndex, 1);
                  } else {
                    newChanges[existingChangeIndex] = {
                      ...existingChange,
                      editedValue: nextValue,
                      predictedValue: existingChange?.predictedValue,
                    };
                  }
                } else {
                  if (safeStr(nextValue) === safeStr(existingChange.newValue)) {
                    const { editedValue, ...restOfChange } = existingChange;
                    newChanges[existingChangeIndex] = restOfChange;
                  } else {
                    newChanges[existingChangeIndex] = {
                      ...existingChange,
                      editedValue: nextValue,
                    };
                  }
                }
              }
              return {
                ...item,
                snapshot: { ...item.snapshot, [colKey]: nextValue },
                changes: newChanges,
              };
            }),
          },
        };
      });
    },
    [setSelectedBatch]
  );
  return { counts, visibleRows, handleCellChange };
}

function ColumnHeader({ label, info, isRestricted, theme }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        userSelect: "none",
      }}
    >
      <Box
        component="span"
        sx={{
          cursor: "default",
          color: isRestricted
            ? theme.palette.brain.table.header.restrictedText
            : "inherit",
        }}
      >
        {label}
      </Box>
      {isRestricted && (
        <LockOutlinedIcon
          sx={{
            fontSize: 11,
            color: theme.palette.brain.table.header.restrictedText,
          }}
        />
      )}
      <Tooltip title={info} arrow placement="top">
        <InfoOutlinedIcon
          sx={{
            fontSize: 13,
            color: theme.palette.brain.table.header.restrictedText,
            cursor: "help",
          }}
        />
      </Tooltip>
    </Box>
  );
}

const BatchTableRow = React.memo(
  ({
    row,
    vIdx,
    theme,
    classes,
    displayedColumns,
    columnData,
    is_editable,
    handleCellChange,
    skuCount = 0,
    skuList,
    handleSkuClick,
    fuseByColumn,
  }) => {
    const debounceTimers = useRef({});
    const latestInputValues = useRef({});
    const [invalidInputs, setInvalidInputs] = useState({});
    const [isExpanded, setIsExpanded] = useState(false); // Localized expansion state
    const rowStatus = row.status;
    const rowComment = rowStatus === "rejected" ? row.comment : null;
    const rowBg =
      vIdx % 2 === 0
        ? theme.palette.brain.drawer.item_background
        : theme.palette.brain.table.row.background;
    const changedMap = useMemo(() => {
      const map = new Map();
      (row.changes || []).forEach((c) => map.set(c.column, c));
      return map;
    }, [row.changes]);
    const renderCellValue = (
      colKey,
      isChanged,
      change,
      has_cell_edit_access,
      curr_column
    ) => {
      const currentValue = row.snapshot?.[colKey] ?? "";
      const isTag = isTagKey(colKey, columnData?.tag_columns);
      let chipWasValue = null;
      let isUserEdited = false;

      const key = `${row.rowId}_${colKey}`;
      const validation = invalidInputs?.[key];
      const is_validating = key in debounceTimers.current ? true : false;
      const fuse = fuseByColumn[colKey];

      if (isChanged && change) {
        if ("editedValue" in change) {
          isUserEdited = true;
          chipWasValue =
            "newValue" in change ? change.newValue : change.oldValue;
        } else {
          chipWasValue = change.oldValue;
        }
      }

      const fmt = (v) => {
        if (isTag) {
          const tags = parseTagValue(v);
          return tags.length > 0 ? tags.join(", ") : "(empty)";
        }
        return valueToString(v);
      };
      const renderOldValueChip = () => (
        <Box
          component="span"
          sx={{
            fontSize: "0.65rem",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            bgcolor: theme.palette.brain.table.row.editedBackground,
            border: `1px solid ${theme.palette.brain.table.row.editedBorder}`,
            color: theme.palette.brain.table.row.editedBorder,
            width: "fit-content",
            borderRadius: "5px",
            px: 0.5,
            py: 0.1,
            mt: has_cell_edit_access ? 0.5 : 0,
          }}
        >
          <Box component="span" sx={{ fontSize: "0.6rem", fontWeight: 700 }}>
            Pred:
          </Box>
          {fmt(change?.predictedValue) || "(empty)"}
        </Box>
      );
      const displayNewValue = (v) => {
        if (has_cell_edit_access) {
          if (isTag) {
            const tagArray = parseTagValue(v);
            return (
              <TagInput
                value={tagArray}
                disabled={false}
                onChange={(nextValue) =>
                  handleCellChange(row, colKey, nextValue)
                }
              />
            );
          } else {
            return (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <input
                  value={currentValue || ""}
                  onChange={(e) => {
                    handleCellChange(row, colKey, e.target.value);

                    const val = e.target.value;
                    const key = `${row.rowId}_${colKey}`;

                    if (curr_column?.can_validate) {
                      latestInputValues.current[key] = val;

                      if (debounceTimers.current[key]) {
                        clearTimeout(debounceTimers.current[key]);
                      }

                      // Start debounce
                      debounceTimers.current[key] = setTimeout(() => {
                        const latestValue = latestInputValues.current[key];

                        const isValid = curr_column.validList.includes(
                          latestValue.toLowerCase()
                        );
                        const results = fuse.search(latestValue);
                        setInvalidInputs((prev) => ({
                          ...prev,
                          [key]: {
                            isValid: isValid,
                            suggestedValue:
                              results.length !== 0 ? results[0].item : null,
                            value: latestValue,
                          },
                        }));

                        delete debounceTimers.current[key];
                      }, 500);
                    }
                  }}
                  placeholder="Enter text..."
                  style={{
                    flex: 1,
                    minWidth: 100,
                    padding: "5px 8px",
                    fontSize: "14px",
                    border: isUserEdited
                      ? `2px solid ${theme.palette.brain.table.row.editedBorder}`
                      : `1px solid ${theme.palette.brain.mis.grey.background1}`,
                    padding: "8px 8px",
                    borderRadius: "6px",
                    color: theme.palette.brain.table.row.text,
                    outline: "none",
                    backgroundColor: theme.palette.brain.table.tableBackground,
                  }}
                />
                {isUserEdited &&
                  (is_validating ? (
                    <CircularProgress
                      size={"14px"}
                      sx={{ color: theme.palette.secondary.main2 }}
                    />
                  ) : validation ? (
                    validation?.isValid ? (
                      <CheckCircleIcon
                        sx={{
                          fontSize: 18,
                          color: theme.palette.brain.green.text3,
                        }}
                      />
                    ) : (
                      <Tooltip
                        title={
                          validation?.suggestedValue
                            ? `${validation?.value} has not been seen before. Did you mean ${validation?.suggestedValue}?`
                            : `${validation?.value} has not been seen before. Please double-check.`
                        }
                      >
                        <ErrorOutlineIcon
                          sx={{
                            fontSize: 15,
                            color: theme.palette.brain.yellow.text,
                          }}
                        />
                      </Tooltip>
                    )
                  ) : null)}
              </Box>
            );
          }
        }

        if (isTag) {
          const tagArray = parseTagValue(v);
          // return tagArray.length > 0 ? tagArray.join(", ") : "";
          return (
            <TagInput value={tagArray} disabled={true} onChange={() => {}} />
          );
        }
        return fmt(v);
      };
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: has_cell_edit_access ? 0 : 0.25,
          }}
        >
          {has_cell_edit_access ? (
            <>
              {displayNewValue(currentValue)}
              {isChanged && chipWasValue !== null && renderOldValueChip()}
            </>
          ) : isChanged ? (
            <>
              <Box
                component="span"
                sx={{
                  fontSize: "0.8rem",
                  color: theme.palette.brain.table.row.text,
                }}
              >
                {displayNewValue(currentValue) || "(empty)"}
              </Box>
              {chipWasValue !== null && renderOldValueChip()}
            </>
          ) : (
            <Box
              component="span"
              sx={{
                color:
                  currentValue !== "" && currentValue !== null
                    ? "inherit"
                    : "text.disabled",
              }}
            >
              {displayNewValue(currentValue) || "-"}
            </Box>
          )}
        </Box>
      );
    };
    return (
      <Fragment>
        <TableRow
          sx={{ backgroundColor: rowBg, "& > td": { backgroundColor: rowBg } }}
        >
          <TableCell
            className={classes.baseCell}
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 2,
              borderRight: `1px solid ${theme.palette.brain.grey.border}`,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor:
                      rowStatus === "approved"
                        ? theme.palette.brain.green.text3
                        : rowStatus === "rejected"
                        ? theme.palette.brain.red.text
                        : theme.palette.brain.yellow.text,
                  }}
                />
                {skuList?.length === 0 ? (
                  "-"
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <TagInput
                      value={skuList}
                      disabled={true}
                      onChange={() => {}}
                      maxVisible={1}
                      tagLabel={"All SKUs"}
                    />
                    <Box>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSkuClick(
                            row.snapshot.upc12,
                            row.snapshot.description,
                            row.rowId
                          );
                        }}
                        size="small"
                      >
                        <VisibilityOutlinedIcon
                          sx={{
                            fontSize: "16px",
                            color: theme.palette.secondary.main2,
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box>
                {rowComment && (
                  <Box
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      color: theme.palette.brain.rejected.primaryText,
                      cursor: "pointer",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: theme.palette.brain.rejected.background,
                      border: `1px solid ${theme.palette.brain.rejected.border}`,
                    }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 11 }} /> Reason
                    {isExpanded ? (
                      <ExpandLessIcon sx={{ fontSize: 13 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 13 }} />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </TableCell>
          {displayedColumns.map((col) => {
            const isRestricted = isRestrictedKey(
              col.key,
              columnData?.restricted_columns
            );
            const change = changedMap.get(col.key);
            const isChanged = !!change;
            const has_cell_edit_access =
              is_editable &&
              (rowStatus === "pending" || rowStatus === "rejected") &&
              columnData?.editable_columns?.includes(col.key);
            return (
              <TableCell
                key={col.key}
                className={classes.baseCell}
                sx={{
                  bgcolor: isRestricted
                    ? "rgba(148, 163, 184, 0.05)"
                    : change?.editedValue
                    ? theme.palette.brain.table.row.editedBackground
                    : undefined,
                  borderLeft: isChanged
                    ? `3px solid ${theme.palette.brain.table.row.editedBorder}`
                    : "3px solid transparent",
                  color:
                    isRestricted && !isChanged
                      ? theme.palette.brain.table.header.restrictedText
                      : theme.palette.brain.table.row.text,
                  cursor: isRestricted ? "not-allowed" : "default",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                }}
              >
                {renderCellValue(
                  col.key,
                  isChanged,
                  change,
                  has_cell_edit_access,
                  col
                )}
              </TableCell>
            );
          })}
        </TableRow>
        {rowComment && (
          <TableRow
            sx={{
              "& > td": { p: 0, borderBottom: "none", backgroundColor: rowBg },
            }}
          >
            <TableCell
              colSpan={displayedColumns.length + 1}
              sx={{ position: "sticky", left: 0 }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    m: 1,
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: theme.palette.brain.rejected.background,
                    border: `1px solid ${theme.palette.brain.rejected.border}`,
                    borderLeft: `3px solid ${theme.palette.brain.rejected.secondaryBorder}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.brain.rejected.primaryText,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      display: "block",
                      fontSize: "0.62rem",
                      mb: 0.5,
                    }}
                  >
                    Approver feedback for this row
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.brain.rejected.secondaryText,
                      fontSize: "0.78rem",
                    }}
                  >
                    {rowComment}
                  </Typography>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  }
);

function SubmissionDetailPage() {
  const dispatch = useDispatch();
  const columnData = useSelector((state) => state.brain.columnData);
  const param = useParams();
  const submissionId = param?.id;
  const [selectedBatch, setSelectedBatch] = useState({
    status: "initial",
    data: {},
    error: "",
    preview_mode: "read-only",
  });

  const fuseByColumn = useMemo(() => {
    const result = {};

    columnData.columns.forEach((column) => {
      if (column.can_validate) {
        result[column.key] = new Fuse(column.validList, {
          threshold: 0.4,
          includeScore: true,
        });
      }
    });

    return result;
  }, [columnData]);

  const theme = useTheme();
  const classes = brainStyles(theme);
  const navigate = useNavigate();
  const is_editable = useMemo(() => {
    return selectedBatch?.preview_mode === "edit";
  }, [selectedBatch]);
  const [tab, setTab] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skuDrawer, setSkuDrawer] = useState({
    open: false,
    data: {},
  });
  const { visibleColumns, setVisibleColumns, columnOrder, setColumnOrder } =
    useTableColumns(columnData);
  const { counts, visibleRows, handleCellChange } = useBatchData(
    selectedBatch,
    setSelectedBatch
  );
  const displayedColumns = useMemo(() => {
    return columnOrder
      .filter((key) => visibleColumns.has(key))
      .map((key) => columnData?.columns?.find((col) => col.key === key))
      .filter(Boolean);
  }, [columnOrder, visibleColumns, columnData]);
  const isLoading =
    selectedBatch?.status === "loading" || selectedBatch?.status === "initial";

  const loadColumnData = async () => {
    try {
      const resp = await getReviewColumns();

      const statusCode = resp.data.status.statusCode;
      if (statusCode === "nam-200") {
        const columns = resp.data.data?.upc_columns;
        const reference_columns = columns
          .filter((item) => !item.is_editable_column)
          .map((item, _) => item.key);
        const restricted_columns = columns
          .filter((item) => item.is_editable_column && !item.user_edit_access)
          .map((item, _) => item.key);
        const editable_columns = columns
          .filter((item) => item.user_edit_access)
          .map((item, _) => item.key);

        dispatch(
          updateReviewColumnData({
            columns: columns,
            key_reference_columns: APP_CONSTANTS.KEY_REFERENCE_COLUMNS,
            reference_columns: reference_columns,
            tag_columns: APP_CONSTANTS.TAG_COLUMNS,
            restricted_columns: restricted_columns,
            editable_columns: editable_columns,
            sku_columns: resp.data.data?.sku_columns,
          })
        );
      }
    } catch (e) {}
  };

  const fetchIndividualSubmissionDataById = async () => {
    try {
      setSelectedBatch({
        status: "loading",
        data: {},
        error: "",
        preview_mode: "read-only",
      });
      const response = await fetchIndividualSubmissionData(submissionId);
      if (response?.data?.status?.statusCode === "nam-200") {
        setSelectedBatch({
          status: "success",
          data: response?.data?.data,
          error: "",
          preview_mode: "read-only",
        });
      } else {
        setSelectedBatch({
          status: "error",
          data: {},
          error:
            response?.data?.status?.statusMessage || "Internal Server Error",
          preview_mode: "read-only",
        });
      }
    } catch (error) {
      setSelectedBatch({
        status: "error",
        data: {},
        error: "Internal Server Error",
        preview_mode: "read-only",
      });
    }
  };

  const exportToExcel = async (
    downloadableColumns,
    skuColumns,
    rowData,
    upc_skuMap,
    fileName = "downloaded_file",
    sheetName = "Data"
  ) => {
    if (!rowData?.length) {
      dispatch(
        updateAlertInfo({
          message: "No data available to export",
          open: true,
          severity: "error",
        })
      );
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName, {
      views: [
        {
          state: "frozen",
          xSplit: 1, // Freeze Status column
          ySplit: 1, // Freeze Header row
        },
      ],
    });

    const upcColumns = ["status", ...downloadableColumns];
    const allColumns = [...upcColumns, ...skuColumns];

    // HEADER
    worksheet.addRow(allColumns);
    const headerRow = worksheet.getRow(1);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "ECF0F5" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
        bottom: { style: "thin" },
      };
    });

    let currentRowNumber = 2;

    // DATA ROWS
    rowData.forEach((record) => {
      const rowId = record.rowId;
      const skus =
        upc_skuMap[rowId] && upc_skuMap[rowId].length > 0
          ? upc_skuMap[rowId]
          : [{}];

      const numSkuRows = skus.length;
      const startRow = currentRowNumber;
      const endRow = currentRowNumber + numSkuRows - 1;

      // Build the UPC base values
      const upcValues = [
        record.status,
        ...downloadableColumns.map((column) => {
          let value = record.snapshot?.[column];
          return getParsedValue(column, value);
        }),
      ];

      // 2. WRITE INDIVIDUAL ROWS FOR EACH SKU
      skus.forEach((sku) => {
        const skuValues = skuColumns.map((col) => sku[col] || "");
        const rowValues = [...upcValues, ...skuValues];
        worksheet.addRow(rowValues);
      });

      // 3. MERGE UPC COLUMNS IF THERE ARE MULTIPLE SKUs
      if (numSkuRows > 1) {
        for (let colIdx = 1; colIdx <= upcColumns.length; colIdx++) {
          worksheet.mergeCells(startRow, colIdx, endRow, colIdx);

          // Vertically center the merged text for better readability
          const mergedCell = worksheet.getCell(startRow, colIdx);
          mergedCell.alignment = { vertical: "middle", horizontal: "left" };
        }
      }

      // 4. APPLY STYLING (Apply only to the top-left cell of the merge: startRow)
      const statusCell = worksheet.getCell(startRow, 1);

      if (record.status === "approved") {
        statusCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFC6EFCE" },
        };
      } else if (record.status === "rejected") {
        statusCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFC7CE" },
        };
      } else if (record.status === "pending") {
        statusCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFF2CC" },
        };
      }

      // HIGHLIGHT CHANGED CELLS
      (record.changes || []).forEach((change) => {
        const columnIndex =
          allColumns.findIndex((column) => column === change.column) + 1;

        if (columnIndex <= 0) return;

        const cell = worksheet.getCell(startRow, columnIndex);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFF2CC" },
        };

        cell.note = {
          texts: [
            {
              text: `Predicted Value: ${
                getParsedValue(change.column, change.predictedValue) ?? "null"
              }`,
            },
          ],
          author: "DART",
        };
      });

      // Advance the current row tracker for the next UPC iteration
      currentRowNumber += numSkuRows;
    });

    // AUTO SIZE COLUMNS
    worksheet.columns.forEach((column) => {
      let maxLength = 15;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const value = cell.value?.toString() || "";
        maxLength = Math.max(maxLength, value.length + 2);
      });
      column.width = Math.min(maxLength, 60);
    });

    // DOWNLOAD FILE
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${fileName}.xlsx`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const openSkuForUpc = (upc12, description, row_id) => {
    const sku_rows = selectedBatch.data.upc_sku_map[row_id] || [];
    const sku_columns = columnData.sku_columns;
    setSkuDrawer({
      open: true,
      data: {
        row_id: row_id,
        upc: upc12,
        description: description,
        sku_details: {
          rows: sku_rows,
          columns: sku_columns,
        },
      },
    });
  };
  useEffect(() => {
    if (columnData?.columns?.length === 0) {
      loadColumnData();
    }
    fetchIndividualSubmissionDataById();
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    let payloadData = [];
    selectedBatch?.data?.rows?.forEach((item) => {
      (item.changes || []).forEach((cur_change) => {
        if (cur_change.hasOwnProperty("editedValue")) {
          payloadData.push({
            rowId: item?.rowId,
            column: cur_change?.column,
            oldValue: cur_change?.is_new_object
              ? cur_change?.oldValue
              : cur_change?.newValue,
            newValue: cur_change?.editedValue,
          });
        }
      });
    });
    try {
      const response = await resubmitSubmissionData({
        payload: payloadData,
        submission_id: submissionId,
      });

      if (response.data.status.statusCode === "nam-200") {
        dispatch(
          updateAlertInfo({
            message: "Submission submitted successfully",
            open: true,
            severity: "success",
          })
        );
        fetchIndividualSubmissionDataById();
      } else {
        dispatch(
          updateAlertInfo({
            message:
              "Failed to re-submit your submission, please try again later!",
            open: true,
            severity: "error",
          })
        );
      }
    } catch (e) {
      console.error("ERROR", e);
    }
    setIsSubmitting(false);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "24px 24px 16px 24px",
      }}
    >
      {isLoading ? (
        <Loader message={"Loading ..."} />
      ) : selectedBatch.error ? (
        <Error
          message={selectedBatch.error || ""}
          fontSize={theme.size[16]}
          margin={theme.margin[1]}
          imageHeight={"30vh"}
          errHeight={"70vh"}
          paddingTop={theme.size[200]}
        />
      ) : (
        <>
          {/* Header, Download Button */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography
                  variant="h1"
                  component="span"
                  sx={{
                    fontSize: "1.25rem",
                    color: theme.palette.secondary.main2,
                  }}
                >
                  Submission #{selectedBatch?.data?.id}
                </Typography>
                <StatusChip status={selectedBatch?.data?.status} />
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.75 }}
              >
                <Typography variant="caption" color="text.secondary">
                  <strong>{selectedBatch?.data?.total_rows}</strong> row
                  {selectedBatch?.data?.total_rows > 1 ? "s" : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>{selectedBatch?.data?.total_changes}</strong> change
                  {selectedBatch?.data?.total_changes === 1 ? "" : "s"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted{" "}
                  {selectedBatch?.data?.submittedAt &&
                    new Date(selectedBatch.data.submittedAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} size={16} />}
                onClick={() => {
                  const downloadableColumns = displayedColumns.map(
                    (item) => item.key
                  );
                  const rowData = selectedBatch?.data?.rows || [];
                  const todaysDate = new Intl.DateTimeFormat("en-CA").format(
                    new Date()
                  );
                  const fileName = `BRAIN_Submission_ID_${selectedBatch?.data?.id}_${todaysDate}`;
                  const sheetName = "Submission Data";

                  const upc_skuMap = selectedBatch.data.upc_sku_map || [];
                  const sku_visible_columns = getLocalStorageData(
                    APP_CONSTANTS.VISIBLE_SKU_COLUMN
                  );

                  exportToExcel(
                    downloadableColumns,
                    sku_visible_columns,
                    rowData,
                    upc_skuMap,
                    fileName,
                    sheetName
                  );
                }}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  borderColor: theme.palette.brain.table.row.text,
                  color: theme.palette.brain.table.row.text,
                  "&:hover": {
                    borderColor: theme.palette.secondary.main2,
                    color: theme.palette.secondary.main2,
                  },
                }}
              >
                Export to Excel
              </Button>
              <ColumnVisibilityControl
                columns={columnData?.columns || []}
                allowedColumns={columnData?.editable_columns}
                keyRefColumns={columnData?.key_reference_columns}
                refColumns={columnData?.reference_columns}
                restrictedColumns={columnData?.restricted_columns}
                visible={visibleColumns}
                onChange={setVisibleColumns}
                columnOrder={columnOrder}
                onReorder={setColumnOrder}
                theme={theme}
                visibleColumnKey={APP_CONSTANTS.VISIBLE_COLUMN}
                columnOrderKey={APP_CONSTANTS.COLUMN_ORDER}
              />

              {["pending", "re-submitted", "returned_for_review"].includes(
                selectedBatch.data.status
              ) && (
                <>
                  {selectedBatch?.preview_mode === "edit" ? (
                    <Button
                      onClick={() =>
                        setSelectedBatch((prev) => {
                          return { ...prev, preview_mode: "read-only" };
                        })
                      }
                      variant="outlined"
                      size="small"
                      sx={{
                        textTransform: "none",
                        borderColor: theme.palette.secondary.main2,
                        color: theme.palette.secondary.main2,
                        "&:hover": {
                          borderColor: theme.palette.secondary.main2,
                        },
                        width: "120px",
                      }}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      // disabled={!canEditSubmission}
                      onClick={() =>
                        setSelectedBatch((prev) => {
                          return { ...prev, preview_mode: "edit" };
                        })
                      }
                      sx={{
                        textTransform: "none",
                        bgcolor: theme.palette.brain.green.text,
                        "&:hover": { bgcolor: theme.palette.brain.green.text },
                      }}
                    >
                      Edit Submission
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          {selectedBatch?.data?.comment && (
            <Box
              sx={{
                // mb: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: theme.palette.brain.rejected.background,
                border: `1px solid ${theme.palette.brain.rejected.border}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <ChatBubbleOutlineIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.brain.rejected.primaryText,
                  mt: "2px",
                }}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.brain.rejected.primaryText,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    fontSize: "0.62rem",
                  }}
                >
                  Approver overall comment
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.brain.rejected.secondaryText,
                    fontSize: "0.78rem",
                  }}
                >
                  {selectedBatch.data.comment}
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `1px solid ${theme.palette.brain.grey.border}`,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  textTransform: "none",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  px: 1.75,
                  color: theme.palette.brain.table.row.text,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.secondary.main2,
                  height: 2.5,
                },
              }}
            >
              <Tab label={`All (${counts.all})`} value="all" />
              <Tab label={`Pending (${counts.pending})`} value="pending" />
              <Tab label={`Approved (${counts.approved})`} value="approved" />
              <Tab label={`Rejected (${counts.rejected})`} value="rejected" />
            </Tabs>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  fontSize: "0.78rem",
                  color: "text.secondary",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: theme.palette.brain.green.text3,
                    }}
                  />
                  <span>{counts.approved} approved</span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: theme.palette.brain.red.text,
                    }}
                  />
                  <span>{counts.rejected} rejected</span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: theme.palette.brain.yellow.text,
                    }}
                  />
                  <span>{counts.pending} pending</span>
                </Box>
              </Stack>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: theme.palette.brain.table.row.editedBackground,
                    border: `1px solid ${theme.palette.brain.table.row.editedBorder}`,
                  }}
                />
                <Typography variant="caption" color={"text.secondary"}>
                  Changed
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LockOutlinedIcon
                  sx={{ fontSize: 12, color: theme.palette.brain.grey.text }}
                />
                <Typography variant="caption" color="text.secondary">
                  Restricted
                </Typography>
              </Box>
            </Box>
          </Box>

          {visibleRows?.[tab].length === 0 ? (
            <Box
              sx={{
                border: `1px dashed ${theme.palette.brain.grey.border}`,
                borderRadius: 1.5,
                py: 6,
                px: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                bgcolor: theme.palette.brain.table.background1,
                flex: 1,
              }}
            >
              <InboxOutlinedIcon
                sx={{
                  fontSize: 40,
                  color: theme.palette.secondary.main2,
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.brain.table.header.text,
                }}
              >
                No {tab === "all" ? "" : tab} rows
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1,
                  marginBottom: "4px",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 13, color: "#b45309" }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    color: "#92400e",
                  }}
                >
                  These are AI-generated product attributes. Please review and
                  validate them before approval.
                </Typography>
              </Box>
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
                  backgroundColor: theme.palette.brain.table.tableBackground,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { height: 6, width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.brain.mis.grey.background1,
                    borderRadius: 3,
                  },
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={classes.tableHeader}
                        sx={{
                          minWidth: 175,
                          position: "sticky",
                          left: 0,
                          top: 0,
                          zIndex: 4,
                          borderRight: `1px solid ${theme.palette.brain.grey.border}`,
                        }}
                      >
                        Row Status
                      </TableCell>
                      {displayedColumns?.map((col) => {
                        const isRestricted = isRestrictedKey(
                          col.key,
                          columnData?.restricted_columns
                        );
                        return (
                          <TableCell
                            key={col.key}
                            className={
                              isRestricted
                                ? classes.restrictedHeader
                                : classes.tableHeader
                            }
                            sx={{
                              minWidth: col.key === "Description" ? 220 : 150,
                              position: "sticky",
                              top: 0,
                              zIndex: 3,
                            }}
                          >
                            <ColumnHeader
                              label={col.label}
                              info={col.info}
                              isRestricted={isRestricted}
                              theme={theme}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows?.[tab].map((row, vIdx) => (
                      <BatchTableRow
                        key={row.rowId}
                        row={row}
                        vIdx={vIdx}
                        theme={theme}
                        classes={classes}
                        displayedColumns={displayedColumns}
                        columnData={columnData}
                        is_editable={is_editable}
                        handleCellChange={handleCellChange}
                        skuCount={
                          selectedBatch.data.upc_sku_map[row.rowId].length
                        }
                        skuList={(
                          selectedBatch.data.upc_sku_map?.[row.rowId] || []
                        ).map((item, _) => item.sku)}
                        handleSkuClick={openSkuForUpc}
                        fuseByColumn={fuseByColumn}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              onClick={() => navigate("/brain/submissions")}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: theme.palette.secondary.main2,
                color: theme.palette.secondary.main2,
                "&:hover": { borderColor: theme.palette.secondary.main2 },
                width: "120px",
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              // disabled={isSubmitDisabled}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                background: theme.palette.secondary.main2,
                "&:hover": { background: theme.palette.secondary.main2 },
                color: "#fff",
              }}
            >
              Submit Changes
            </Button>
          </Box>

          {skuDrawer?.open && (
            <SKUDetailsDrawer
              open={skuDrawer?.open}
              data={skuDrawer.data}
              onClose={() => {
                setSkuDrawer({ open: false, data: {} });
              }}
            />
          )}
        </>
      )}

      {isSubmitting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10001,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            bgcolor: theme.palette.brain.mis.grey.disabledBackground,
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress
            size={24}
            sx={{ color: theme.palette.secondary.main2 }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.brain.table.header.text,
            }}
          >
            Saving changes...
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default SubmissionDetailPage;
